import { List, Map, MapPinned, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CapitalAreaExplorer } from "./components/CapitalAreaExplorer";
import { FilterPanel } from "./components/FilterPanel";
import { MapView } from "./components/MapView";
import { PlaceCard } from "./components/PlaceCard";
import { ViewRankings } from "./components/ViewRankings";
import { PlaceDetailModal } from "./components/PlaceDetailModal";
import { RegionNavigator } from "./components/RegionNavigator";
import { WeeklyRecommendations } from "./components/WeeklyRecommendations";
import { categoryById } from "./data/categories";
import { areaGroupLabels, regionLabels, weekdayLabels, stayTimeLabels, priceLabels, indoorOutdoorLabels } from "./lib/labels";
import { places as seedPlaces } from "./data/places";
import { areaGroups as areaGroupDefinitions, regionById, regions as regionDefinitions } from "./data/regions";
import type { AreaGroupId, District, FilterState, Place, RegionId } from "./data/schema";
import { trackPlaceView } from "./lib/analytics";
import { filterPlaces } from "./lib/filtering";
import { fetchRemotePlaces } from "./lib/remotePlaces";

const PAGE_SIZE = 12;

const fallbackPlaces = seedPlaces.filter((place) => !place.summary.includes("샘플"));

const defaultFilters: FilterState = {
  query: "",
  areaGroups: [],
  regions: [],
  categories: [],
  weekdays: [],
  districts: [],
  indoorOutdoor: "all",
  priceType: "all",
  minAgeFit: 1,
  rainFriendlyOnly: false,
  stayTimes: [],
};

export default function App() {
  const [places, setPlaces] = useState<Place[]>(fallbackPlaces);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [selectedPlace, setSelectedPlace] = useState<Place | undefined>();
  const [loadState, setLoadState] = useState<"loading" | "ready" | "fallback">("loading");
  const [sortOrder, setSortOrder] = useState<"name" | "region">("name");
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [detailPlace, setDetailPlace] = useState<Place | undefined>();

  useEffect(() => {
    let ignore = false;

    fetchRemotePlaces()
      .then((payload) => {
        if (ignore) return;
        setPlaces(payload.places);
        setLoadState(payload.source === "d1" ? "ready" : "fallback");
      })
      .catch(() => {
        if (ignore) return;
        setPlaces(fallbackPlaces);
        setLoadState("fallback");
      });

    return () => {
      ignore = true;
    };
  }, []);

  const filteredPlaces = useMemo(() => filterPlaces(places, filters).sort((a, b) => {
    if (sortOrder === "region") {
      const difference = regionLabels[a.region].localeCompare(regionLabels[b.region], "ko");
      if (difference) return difference;
    }
    return a.name.localeCompare(b.name, "ko");
  }), [filters, places, sortOrder]);
  const pageKey = JSON.stringify([filters, sortOrder]);
  useEffect(() => {
    setPageState({ key: pageKey, page: 1 });
  }, [pageKey]);
  const pageCount = Math.max(1, Math.ceil(filteredPlaces.length / PAGE_SIZE));
  const currentPage = pageState.key === pageKey ? Math.min(pageState.page, pageCount) : 1;
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visiblePlaces = filteredPlaces.slice(pageStart, pageStart + PAGE_SIZE);
  const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1)
    .filter((page) => page === 1 || page === pageCount || Math.abs(page - currentPage) <= 1);

  function changePage(page: number) {
    setPageState({ key: pageKey, page });
    requestAnimationFrame(() => {
      document.getElementById("places")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const closeDetail = useCallback(() => setDetailPlace(undefined), []);
  const activeFilters = [
    ...(filters.query ? [{ label: `검색: ${filters.query}`, clear: () => setFilters({ ...filters, query: "" }) }] : []),
    ...filters.areaGroups.map((id) => ({ label: areaGroupLabels[id], clear: () => setFilters({ ...filters, areaGroups: filters.areaGroups.filter((value) => value !== id), regions: [], districts: [] }) })),
    ...filters.regions.map((id) => ({ label: regionLabels[id], clear: () => setFilters({ ...filters, regions: filters.regions.filter((value) => value !== id), districts: [] }) })),
    ...filters.categories.map((id) => ({ label: categoryById[id].shortName, clear: () => setFilters({ ...filters, categories: filters.categories.filter((value) => value !== id) }) })),
    ...filters.weekdays.map((id) => ({ label: `${weekdayLabels[id]}요일`, clear: () => setFilters({ ...filters, weekdays: filters.weekdays.filter((value) => value !== id) }) })),
    ...filters.districts.map((id) => ({ label: id, clear: () => setFilters({ ...filters, districts: filters.districts.filter((value) => value !== id) }) })),
    ...filters.stayTimes.map((id) => ({ label: stayTimeLabels[id], clear: () => setFilters({ ...filters, stayTimes: filters.stayTimes.filter((value) => value !== id) }) })),
    ...(filters.priceType !== "all" ? [{ label: priceLabels[filters.priceType], clear: () => setFilters({ ...filters, priceType: "all" }) }] : []),
    ...(filters.indoorOutdoor !== "all" ? [{ label: indoorOutdoorLabels[filters.indoorOutdoor], clear: () => setFilters({ ...filters, indoorOutdoor: "all" }) }] : []),
    ...(filters.rainFriendlyOnly ? [{ label: "비 오는 날", clear: () => setFilters({ ...filters, rainFriendlyOnly: false }) }] : []),
  ];
  const capitalAreaActive =
    filters.areaGroups.includes("capital") ||
    filters.regions.some((region) => regionById[region].areaGroup === "capital");
  const availableRegions = useMemo(
    () =>
      regionDefinitions
        .map((region) => region.id)
        .filter((region) => places.some((place) => place.region === region)),
    [places],
  );
  const availableAreaGroups = useMemo(
    () =>
      areaGroupDefinitions
        .map((areaGroup) => areaGroup.id)
        .filter((areaGroup) =>
          places.some((place) => regionById[place.region].areaGroup === areaGroup),
        ),
    [places],
  );
  const districts = useMemo(
    () =>
      Array.from(
        new Set(
          places
            .filter(
              (place) =>
                (!filters.areaGroups.length ||
                  filters.areaGroups.includes(regionById[place.region].areaGroup)) &&
                (!filters.regions.length || filters.regions.includes(place.region)),
            )
            .map((place) => place.district),
        ),
      ).sort((a, b) => a.localeCompare(b, "ko")) as District[],
    [filters.areaGroups, filters.regions, places],
  );

  function handleSelect(place: Place) {
    setSelectedPlace(place);
    setDetailPlace(place);
    void trackPlaceView(place.id).catch(() => {
      // Analytics should never interrupt place exploration.
    });
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-place-id="${place.id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  return (
    <main className="app">
      <nav className="tour-nav" aria-label="브랜드 메뉴">
        <a className="tour-logo" href="https://www.askpapa.net/" aria-label="askpapa 대표 페이지">
          <img src="/brand/symbol.svg" width="37" height="37" alt="" />
          <span>askpapa</span><small>여행</small>
        </a>
        <span className="nav-explore">아빠와 아이들</span>
      </nav>
      <header className="app-header">
        <div className="header-copy">
          <span className="hero-kicker">아빠와 아이들의 국내여행</span>
          <h1>이번에는,<br />어디로 갈까요?</h1>
          <p>가까운 나들이부터 새로운 지역까지.</p>
          <form className="hero-search" role="search" onSubmit={(event) => {
            event.preventDefault();
            document.getElementById("places")?.scrollIntoView({ behavior: "smooth" });
          }}>
            <Search size={21} aria-hidden="true" />
            <input aria-label="여행지 검색" placeholder="어디로 가고 싶으세요?" value={filters.query}
              onChange={(event) => setFilters({ ...filters, query: event.target.value })} />
            <button type="submit">찾기</button>
          </form>
          <div className="hero-shortcuts" aria-label="빠른 필터">
            <button type="button" aria-pressed={filters.rainFriendlyOnly} onClick={() => setFilters({ ...filters, rainFriendlyOnly: !filters.rainFriendlyOnly })}>비 오는 날</button>
            <button type="button" aria-pressed={filters.priceType === "free"} onClick={() => setFilters({ ...filters, priceType: filters.priceType === "free" ? "all" : "free" })}>무료 나들이</button>
            <button type="button" aria-pressed={filters.indoorOutdoor === "outdoor"} onClick={() => setFilters({ ...filters, indoorOutdoor: filters.indoorOutdoor === "outdoor" ? "all" : "outdoor" })}>야외 활동</button>
          </div>
        </div>
        <div className="tour-hero-art" aria-hidden="true">
          <svg viewBox="0 0 420 280" fill="none">
            <circle cx="317" cy="65" r="33" fill="#F4C84A" />
            <path d="M10 232 125 74l91 126 71-87 123 119v48H10Z" fill="#CAD6FF" />
            <path d="m0 254 112-101 109 85 71-58 128 74v26H0Z" fill="#8FA9FB" />
            <path d="M178 280c-75-64 147-46 81-112" stroke="white" strokeWidth="16" strokeLinecap="round" />
            <path d="M98 147V88a37 37 0 0 1 74 0v59" stroke="#2855E8" strokeWidth="25" strokeLinecap="round" />
            <path d="M153 147v-31a22 22 0 0 1 44 0v31" stroke="#F4C84A" strokeWidth="23" strokeLinecap="round" />
            <path d="m325 202 0-55m-17 17 17-17 17 17" stroke="#2855E8" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="52" cy="221" r="5" fill="#2855E8" />
          </svg>
        </div>
      </header>

      <details className="age-preparation">
        <summary>아이 연령별 나들이 준비 <span>영아기 · 유아기 · 초등 저학년</span></summary>
        <div className="age-preparation-grid">
          <div><strong>영아기</strong><small>만 0~2세 · 출생~35개월</small><p>이동 시간, 수유·기저귀 교환 공간, 유모차 동선을 확인하세요.</p></div>
          <div><strong>유아기</strong><small>만 3세~초등 입학 전</small><p>참여 가능 연령과 키 제한, 휴식 공간을 확인하세요.</p></div>
          <div><strong>초등 저학년</strong><small>초등학교 1~3학년</small><p>관심 있는 전시나 체험을 고르고 예약 조건을 확인하세요.</p></div>
        </div>
        <p className="age-preparation-note">나들이 준비 안내입니다. 장소별 연령 적합성은 아직 검토 중입니다.</p>
      </details>
      {loadState !== "ready" && <p className="data-notice" role="status">{loadState === "loading" ? "여행지를 불러오는 중입니다." : "최신 정보를 불러오지 못해 기본 목록을 표시합니다. 운영 정보는 공식 안내를 확인하세요."}</p>}
      <RegionNavigator filters={filters} onChange={setFilters} places={places} />

      {capitalAreaActive && (
        <CapitalAreaExplorer
          filters={filters}
          onChange={setFilters}
          onShowMap={() => {
            setViewMode("map");
            requestAnimationFrame(() => {
              document.querySelector(".explore-toolbar")?.scrollIntoView({ behavior: "smooth" });
            });
          }}
          places={places}
        />
      )}

      <section className="explore-toolbar" aria-label="탐색 보기 설정">
        <div>
          <strong aria-live="polite">여행지 {filteredPlaces.length}곳</strong>
          <span>
            {activeFilters.length ? `선택한 조건 ${activeFilters.length}개` : "전체 지역 · 전체 활동"}
          </span>
        </div>
        <div className="explore-controls">
        <label className="sort-control"><span>정렬</span><select aria-label="여행지 정렬" value={sortOrder} onChange={(event) => setSortOrder(event.target.value as "name" | "region")}><option value="name">이름순</option><option value="region">지역순</option></select></label>
        <div className="view-toggle" role="tablist" aria-label="보기 방식">
          {[
            { id: "list", label: "목록", icon: List },
            { id: "map", label: "지도", icon: Map },
          ].map(({ id, label, icon: Icon }) => (
            <button
              aria-selected={viewMode === id}
              className={viewMode === id ? "active" : ""}
              key={id}
              onClick={() => setViewMode(id as "list" | "map")}
              role="tab"
              type="button"
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
        </div>
      </section>
      {activeFilters.length > 0 && <div className="active-filters" aria-label="선택한 조건">
        {activeFilters.map((filter, index) => <button type="button" key={`${filter.label}-${index}`} onClick={filter.clear} aria-label={`${filter.label} 조건 해제`}>{filter.label}<X size={12} aria-hidden="true" /></button>)}
        <button type="button" className="clear-filters" onClick={() => setFilters(defaultFilters)}>전체 해제</button>
      </div>}

      <div className="workspace">
        <div className={`filter-shell${filtersExpanded ? " expanded" : ""}`}>
        <button type="button" className="mobile-filter-button" aria-expanded={filtersExpanded} aria-controls="tour-filters" onClick={() => setFiltersExpanded(!filtersExpanded)}>상세 조건 {filtersExpanded ? "접기 −" : "열기 +"}</button>
        <div id="tour-filters">
        <FilterPanel
          areaGroups={availableAreaGroups as AreaGroupId[]}
          districts={districts}
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(defaultFilters)}
          regions={availableRegions as RegionId[]}
          resultCount={filteredPlaces.length}
        />
        </div>
        </div>

        <div className="main-column">
          {viewMode === "map" && (
            <MapView
              onSelect={handleSelect}
              places={filteredPlaces}
              selectedPlace={filteredPlaces.find((place) => place.id === selectedPlace?.id)}
            />
          )}

          <section className="places-section" id="places" aria-label="장소 목록">
            <div className="section-heading">
              <MapPinned size={20} aria-hidden="true" />
              <div>
                <h2>아이와 가볼 곳</h2>
                <p>지역과 조건에 맞는 여행지를 살펴보세요.</p>
              </div>
              <span>{filteredPlaces.length}곳</span>
            </div>
            <div className="place-list">
              {visiblePlaces.map((place) => (
                <div data-place-id={place.id} key={place.id}>
                  <PlaceCard
                    onSelect={handleSelect}
                    place={place}
                    selected={selectedPlace?.id === place.id}
                  />
                </div>
              ))}
              {filteredPlaces.length === 0 && (
                <div className="empty-state">
                  <strong>조건에 맞는 여행지가 없습니다.</strong><p>검색어나 선택한 조건을 바꿔보세요.</p><button type="button" onClick={() => setFilters(defaultFilters)}>조건 초기화</button>
                </div>
              )}
            </div>
            {filteredPlaces.length > 0 && <div className="pagination-summary" aria-live="polite">전체 {filteredPlaces.length}곳 중 {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredPlaces.length)}곳</div>}
            {pageCount > 1 && <nav className="pagination" aria-label="여행지 목록 페이지">
              <button type="button" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)} aria-label="이전 페이지">이전</button>
              {pageNumbers.map((page, index) => <span className="pagination-item" key={page}>
                {index > 0 && page - pageNumbers[index - 1] > 1 && <span className="pagination-gap" aria-hidden="true">…</span>}
                <button type="button" aria-label={`${page}페이지`} aria-current={page === currentPage ? "page" : undefined} onClick={() => changePage(page)}>{page}</button>
              </span>)}
              <button type="button" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)} aria-label="다음 페이지">다음</button>
            </nav>}
          </section>

          <WeeklyRecommendations onSelect={handleSelect} places={places} />

          <ViewRankings onSelect={handleSelect} places={places} />
        </div>
      </div>

      <footer className="tour-footer"><a href="https://www.askpapa.net/">askpapa</a><span>아빠와 아이들 · 여행</span><small>요금과 운영 시간은 방문 전 공식 안내를 확인하세요.</small></footer>
      <PlaceDetailModal place={detailPlace} onClose={closeDetail} />
    </main>
  );
}
